/* eslint-disable @typescript-eslint/no-explicit-any */
import { useParams } from "react-router-dom";
import {
  Customer_subscribeAssets,
  Customer_subscribeDevices,
  Customer_unsubscribeAssets,
  Customer_unsubscribeDevices,
  Project_subscribe,
  Project_unsubscribe,
} from "@xompass/web-sdk";
import { chunkArray } from "../utils/arrayUtils";
import { useSocketIOStore } from "./stores/useSocketStore";
import { useRealTimeStore } from "./stores/useRealtimeStore";
import { useAssetsStatusStore } from "./stores/useAssetsStatusStore";
import { RealtimeChannels, Subscription } from "../types/stores/RealtimeStore";
import { Socket } from "socket.io-client";

const GENERAL_MESSAGE_EVENTS = [
  "Asset.change",
  "Asset.container.update",
  "Asset.healthStatus",
  "sensor.alerts",
] as const;

const DATA_EVENTS = [
  "customer.data",
  "asset.data",
  "sensor.data",
  "asset.summaries",
  "sensor.summaries",
] as const;

const UPTIME_EVENTS = [
  "Customer.projects.assets.sensors.uptimeCollectors.create",
  "Project.assets.sensors.uptimeCollectors.create",
  "Asset.sensors.uptimeCollectors.create",
] as const;

const ASSET_EVENT_CHANNELS = [
  "Customer.events.create",
  "Asset.events.create",
  "EventTrigger.events.create",
  "Customer.events.update",
  "Asset.events.update",
  "EventTrigger.events.update",
  "Customer.events.data.create",
  "Asset.events.data.create",
  "EventTrigger.events.data.create",
  "Customer.events.data.upload",
  "Asset.events.data.upload",
  "EventTrigger.events.data.upload",
  "Customer.events.stateChanges.create",
  "Asset.events.stateChanges.create",
  "EventTrigger.events.stateChanges.create",
  "Customer.events.stateChanges.update",
  "Asset.events.stateChanges.update",
  "EventTrigger.events.stateChanges.update",
  "Customer.events.comments.create",
  "Asset.events.comments.create",
  "EventTrigger.events.comments.create",
] as const;

const DEVICE_EVENT_CHANNELS = [
  "Customer.devices.events.create",
  "Device.events.create",
  "Customer.devices.events.update",
  "Device.events.update",
  "Customer.devices.events.data.create",
  "Device.events.data.create",
  "Customer.devices.events.stateChanges.create",
  "Device.events.stateChanges.create",
  "Customer.devices.events.stateChanges.update",
  "Device.events.stateChanges.update",
  "Customer.devices.comments.create",
  "Device.events.comments.create",
] as const;

const HEALTHCHECK_EVENT_CHANNELS = [
  "Customer.healthcheckEvents.create",
  "Project.healthcheckEvents.create",
  "Asset.healthcheckEvents.create",
  "Customer.healthcheckEvents.update",
  "Project.healthcheckEvents.update",
  "Asset.healthcheckEvents.update",
  "Customer.healthcheckEvents.stateChanges.create",
  "Project.healthcheckEvents.stateChanges.create",
  "Asset.healthcheckEvents.stateChanges.create",
  "Customer.healthcheckEvents.stateChanges.update",
  "Project.healthcheckEvents.stateChanges.update",
  "Asset.healthcheckEvents.stateChanges.update",
  "Customer.healthcheckEvents.comments.create",
  "Project.healthcheckEvents.comments.create",
  "Asset.healthcheckEvents.comments.create",
] as const;

export const useSocketIO = () => {
  const params = useParams();
  const customerId = params.customerId || "";
  const projectId = params.projectId || "";

  function initialiseListeners(
    targetSocket = useSocketIOStore.getState().socket,
  ) {
    if (!targetSocket) {
      return () => undefined;
    }

    targetSocket.off("connect", onConnect);
    targetSocket.off("disconnect", onDisconnect);
    targetSocket.off("connect_error", onConnectError);

    targetSocket.on("connect", onConnect);
    targetSocket.on("disconnect", onDisconnect);
    targetSocket.on("connect_error", onConnectError);

    GENERAL_MESSAGE_EVENTS.forEach((event) => {
      targetSocket.off(event, onGeneralMessage);
      targetSocket.on(event, onGeneralMessage);
    });

    targetSocket.off(
      "Project.assets.healthStatus",
      onProjectAssetsHealthStatus,
    );
    targetSocket.on("Project.assets.healthStatus", onProjectAssetsHealthStatus);

    DATA_EVENTS.forEach((event) => {
      targetSocket.off(event, onData);
      targetSocket.on(event, onData);
    });

    UPTIME_EVENTS.forEach((event) => {
      targetSocket.off(event, onUptimeCollector);
      targetSocket.on(event, onUptimeCollector);
    });

    ASSET_EVENT_CHANNELS.forEach((event) => {
      targetSocket.off(event, onAssetEvent);
      targetSocket.on(event, onAssetEvent);
    });

    DEVICE_EVENT_CHANNELS.forEach((event) => {
      targetSocket.off(event, onDeviceEvent);
      targetSocket.on(event, onDeviceEvent);
    });

    HEALTHCHECK_EVENT_CHANNELS.forEach((event) => {
      targetSocket.off(event, onHealthcheckEvent);
      targetSocket.on(event, onHealthcheckEvent);
    });

    return () => removeListeners(targetSocket);
  }

  async function onConnect() {
    const activeSocket = useSocketIOStore.getState().socket;
    if (!activeSocket?.id) {
      return;
    }

    useSocketIOStore.getState().setSocketConnected(true);
    useSocketIOStore.getState().setSocketId(activeSocket.id);
    console.log("Socket connected (React)");
    const activeSubscriptions = useRealTimeStore.getState().activeSubscriptions;
    activeSubscriptions.forEach((config) => {
      void execSubscribe(config, activeSocket);
    });
  }

  function onDisconnect() {
    console.log("Socket disconnected (React)");
    useSocketIOStore.getState().setSocketConnected(false);
    useSocketIOStore.getState().setSocketId(undefined);
  }

  function onConnectError(error: Error) {
    console.log(error);
    useSocketIOStore.getState().setSocketConnected(false);
    useSocketIOStore.getState().setSocketId(undefined);
  }

  function emit(event: string, params: any, targetSocket?: Socket) {
    const socketToUse = targetSocket || useSocketIOStore.getState().socket;
    if (socketToUse) {
      socketToUse.emit(event, params, (err: any) => {
        if (err) {
          console.log(event, err);
        }
      });
    }
  }

  async function unsubscribeProject() {
    const activeSocket = useSocketIOStore.getState().socket;
    if (!projectId || !activeSocket?.id || !activeSocket.connected) {
      return;
    }

    await Project_unsubscribe(projectId, activeSocket.id);
  }

  async function subscribeProject(channels: RealtimeChannels) {
    try {
      const activeSocket = useSocketIOStore.getState().socket;
      if (!projectId || !activeSocket?.id || !activeSocket.connected) {
        console.log("Project or Socket not found");
        return;
      }
      await Project_subscribe(projectId, activeSocket.id);
      activeSocket.emit(
        "subscribe",
        {
          modelName: "Project",
          modelId: projectId,
          channels,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (e: any) => console.log(e),
      );
    } catch (error) {
      console.log(error);
    }
  }

  async function execSubscribe(config: Subscription, targetSocket?: Socket) {
    const activeSocket = targetSocket || useSocketIOStore.getState().socket;
    if (!customerId || !activeSocket?.id || !config?.entity?.modelName) {
      return Promise.resolve();
    }

    const ids = getIds(config.options.id);
    const where = { id: { inq: ids } };

    switch (config.entity.modelName) {
      case "Asset":
        await Customer_subscribeAssets(customerId, activeSocket.id, where);
        break;
      case "Device":
        await Customer_subscribeDevices(customerId, activeSocket.id, where);
        break;
      default:
        return Promise.resolve();
    }

    ids.forEach((id) => {
      emit(
        "subscribe",
        {
          modelName: config.entity.modelName,
          modelId: id,
          channels: config.options.channels || { data: true },
        },
        activeSocket,
      );
    });
    return Promise.resolve();
  }

  async function execUnsubscribe(config: Subscription, targetSocket?: Socket) {
    const activeSocket = targetSocket || useSocketIOStore.getState().socket;
    if (!customerId || !activeSocket?.id) {
      return Promise.resolve();
    }

    const ids = getIds(config.options.id);
    const requests = chunkArray(ids, 100)
      .map((chunk) => {
        const where = { id: { inq: chunk } };
        switch (config.entity.modelName) {
          case "Asset":
            return Customer_unsubscribeAssets(
              customerId,
              activeSocket.id,
              where,
            );
          case "Device":
            return Customer_unsubscribeDevices(
              customerId,
              activeSocket.id,
              where,
            );
          default:
            return null;
        }
      })
      .filter(Boolean) as Promise<void>[];

    await Promise.allSettled(requests);
  }

  function subscribe(
    entity: { modelName?: string } | string,
    options: { id?: string | string[]; channels?: RealtimeChannels },
  ): Promise<any> {
    const config = normalizeSubscription(entity, options);
    if (!config) {
      return Promise.resolve();
    }

    const store = useRealTimeStore.getState();
    if (store.isSubscribed(config)) {
      return Promise.resolve();
    }

    store.addSubscription(config);

    const activeSocket = useSocketIOStore.getState().socket;
    if (!activeSocket?.connected) {
      return Promise.resolve();
    }

    return execSubscribe(config, activeSocket);
  }

  function unsubscribe(
    entity: { modelName?: string } | string,
    options: { id?: string | string[]; channels?: RealtimeChannels },
  ): Promise<any> {
    const config = normalizeSubscription(entity, options);
    if (!config) {
      return Promise.resolve();
    }

    const store = useRealTimeStore.getState();
    const wasSubscribed = store.isSubscribed(config);
    store.removeSubscription(config);

    if (!wasSubscribed) {
      return Promise.resolve();
    }

    const activeSocket = useSocketIOStore.getState().socket;
    if (!activeSocket?.connected) {
      return Promise.resolve();
    }

    return execUnsubscribe(config, activeSocket);
  }

  function onProjectAssetsHealthStatus(event: any) {
    const { body } = event;
    const assetId = Object.keys(body).at(0) as string;
    if (!assetId) {
      return;
    }

    useAssetsStatusStore.getState().updateAssetsStatus(assetId, {
      currentHealthStatus: body[assetId].currentHealthStatus,
      lastCheck: body[assetId].currentHealthStatusDetails.requested.lastCheck,
    });

    onGeneralMessage();
  }

  function onHealthcheckEvent(data: any) {
    switch (data.type) {
      case "Customer.healthcheckEvents.create":
      case "Customer.healthcheckEvents.update":
      case "Customer.healthcheckEvents.comments.create":
      case "Customer.healthcheckEvents.stateChanges.create":
      case "Customer.healthcheckEvents.stateChanges.update":
        onGeneralMessage();
        break;
      case "Project.healthcheckEvents.create":
      case "Project.healthcheckEvents.update":
      case "Project.healthcheckEvents.comments.create":
      case "Project.healthcheckEvents.stateChanges.create":
      case "Project.healthcheckEvents.stateChanges.update":
        onGeneralMessage();
        break;
      case "Asset.healthcheckEvents.create":
      case "Asset.healthcheckEvents.update":
      case "Asset.healthcheckEvents.comments.create":
      case "Asset.healthcheckEvents.stateChanges.create":
      case "Asset.healthcheckEvents.stateChanges.update":
        onGeneralMessage();
        break;
      default:
      //console.log('onHealthCheckEvent default', data);
    }
  }

  function onGeneralMessage(/*event: any*/) {
    //console.log(event.type);
  }

  function onData(data: any) {
    //console.log('data', data);
    //console.log(new Date().toLocaleString('es', { timeZoneName: 'short' }));
    switch (data.type) {
      case "customer.data":
      case "customer.summaries":
        for (const assetId in data.body) {
          for (const sensorId in data.body[assetId]) {
            //console.log('customer summaries');
            broadcastData(data.body[assetId][sensorId], sensorId);
          }
        }
        break;
      case "asset.data":
      case "asset.summaries":
        for (const sensorId in data.body) {
          //console.log('asset summaries');
          broadcastData(data.body[sensorId], sensorId);
        }
        break;
      case "sensor.data":
      case "sensor.summaries": {
        const content = data.body;
        const sensorId = data.from.id;
        //console.log('sensor summaries');
        broadcastData(content, sensorId);
        break;
      }
      default:
      //console.log('onData', data);
    }
  }

  function broadcastData(data: any, sensorId: any) {
    const payload = data?.[0] ? data[0] : data;
    if (!payload) {
      return;
    }

    useRealTimeStore.getState().updateSensorLastData(sensorId, {
      ...payload,
      sensorId: sensorId,
    });

    onGeneralMessage();
  }

  function onAssetEvent(data: any) {
    switch (data.type) {
      case "Customer.events.create":
      case "Customer.events.update":
      case "Customer.events.comments.create":
      case "Customer.events.data.create":
      case "Customer.events.data.upload":
      case "Customer.events.stateChanges.create":
      case "Customer.events.stateChanges.update":
        onGeneralMessage();
        break;
      case "Asset.events.create":
      case "Asset.events.update":
      case "Asset.events.comments.create":
      case "Asset.events.data.create":
      case "Asset.events.data.upload":
      case "Asset.events.stateChanges.create":
      case "Asset.events.stateChanges.update":
        onGeneralMessage();
        break;
      case "EventTrigger.events.create":
      case "EventTrigger.events.update":
      case "EventTrigger.events.comments.create":
      case "EventTrigger.events.data.create":
      case "EventTrigger.events.data.upload":
      case "EventTrigger.events.stateChanges.create":
      case "EventTrigger.events.stateChanges.update":
        onGeneralMessage();
        break;
      default:
      //console.log('onAssetEvent default', data);
    }
  }

  function onUptimeCollector(data: any) {
    //const event = 'io:sensors.uptimeCollectors.create';
    switch (data.type) {
      case "Customer.projects.assets.sensors.uptimeCollectors.create": {
        onGeneralMessage();
        break;
      }
      case "Project.assets.sensors.uptimeCollectors.create": {
        onGeneralMessage();
        break;
      }

      case "Asset.sensors.uptimeCollectors.create": {
        onGeneralMessage();
        break;
      }

      default:
      //console.log('onUptimeCollector default', data);
    }
  }

  function onDeviceEvent(data: any) {
    switch (data.type) {
      case "Customer.devices.events.create":
      case "Customer.devices.events.update":
      case "Customer.devices.events.comments.create":
      case "Customer.devices.events.stateChanges.create":
      case "Customer.devices.events.stateChanges.update": {
        onGeneralMessage();
        break;
      }
      case "Device.events.create":
      case "Device.events.update":
      case "Device.events.comments.create":
      case "Device.events.stateChanges.create":
      case "Device.events.stateChanges.update": {
        onGeneralMessage();
        break;
      }

      case "Device.events.data.create":
      case "Device.events.data.upload": {
        onGeneralMessage();
        break;
      }

      default:
      //console.log('onDeviceEvent default', data);
    }
  }

  function removeListeners(targetSocket?: Socket) {
    if (!targetSocket) {
      return;
    }

    targetSocket.off("connect", onConnect);
    targetSocket.off("disconnect", onDisconnect);
    targetSocket.off("connect_error", onConnectError);

    GENERAL_MESSAGE_EVENTS.forEach((event) => {
      targetSocket.off(event, onGeneralMessage);
    });

    targetSocket.off(
      "Project.assets.healthStatus",
      onProjectAssetsHealthStatus,
    );

    DATA_EVENTS.forEach((event) => {
      targetSocket.off(event, onData);
    });

    UPTIME_EVENTS.forEach((event) => {
      targetSocket.off(event, onUptimeCollector);
    });

    ASSET_EVENT_CHANNELS.forEach((event) => {
      targetSocket.off(event, onAssetEvent);
    });

    DEVICE_EVENT_CHANNELS.forEach((event) => {
      targetSocket.off(event, onDeviceEvent);
    });

    HEALTHCHECK_EVENT_CHANNELS.forEach((event) => {
      targetSocket.off(event, onHealthcheckEvent);
    });
  }

  function normalizeSubscription(
    entity: { modelName?: string } | string,
    options: { id?: string | string[]; channels?: RealtimeChannels },
  ): Subscription | null {
    if (!options || typeof options !== "object" || !options.id) {
      return null;
    }

    const modelName = typeof entity === "string" ? entity : entity?.modelName;
    if (!modelName) {
      return null;
    }

    return {
      entity: { modelName },
      options: {
        id: options.id,
        channels: options.channels,
      },
    };
  }

  function getIds(ids: string | string[]) {
    return Array.isArray(ids) ? ids : [ids];
  }

  return {
    subscribe,
    unsubscribe,
    subscribeProject,
    unsubscribeProject,
    initialiseListeners,
  };
};
